/**
 * Nutrient vector maths.
 *
 * A `Nutrients` value is always a plain vector of numbers with no unit attached.
 * The unit is implied by context:
 *   - on a `Food` it is "per 100 g" or "per 100 ml" (see `Food.basis`)
 *   - on a diary entry's `computed` field it is the absolute amount consumed
 *
 * Keeping it a flat `Record` means every operation below is total and typed:
 * adding a nutrient key to `NUTRIENT_KEYS` makes the compiler point at every
 * place that needs updating.
 */
export const NUTRIENT_KEYS = [
    'kcal',
    'protein',
    'carbs',
    'sugars',
    'fat',
    'saturated',
    'fiber',
    'salt',
];
/** The four values shown on the dashboard rings. */
export const MACRO_KEYS = ['kcal', 'protein', 'carbs', 'fat'];
/** Conversion factor between the two energy units printed on EU labels. */
export const KJ_PER_KCAL = 4.184;
/** Atwater factors: kcal released per gram of each macronutrient. */
export const KCAL_PER_GRAM = {
    protein: 4,
    carbs: 4,
    fat: 9,
};
export function emptyNutrients() {
    return {
        kcal: 0,
        protein: 0,
        carbs: 0,
        sugars: 0,
        fat: 0,
        saturated: 0,
        fiber: 0,
        salt: 0,
    };
}
/**
 * Builds a complete `Nutrients` from a partial one, defaulting anything absent
 * to zero. Used when a source (a label, OpenFoodFacts) only reports some rows.
 */
export function toNutrients(partial) {
    const base = emptyNutrients();
    if (!partial)
        return base;
    for (const key of NUTRIENT_KEYS) {
        const value = partial[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            base[key] = value;
        }
    }
    return base;
}
export function scaleNutrients(nutrients, factor) {
    const result = emptyNutrients();
    for (const key of NUTRIENT_KEYS) {
        result[key] = nutrients[key] * factor;
    }
    return result;
}
export function addNutrients(a, b) {
    const result = emptyNutrients();
    for (const key of NUTRIENT_KEYS) {
        result[key] = a[key] + b[key];
    }
    return result;
}
export function sumNutrients(items) {
    return items.reduce((acc, item) => addNutrients(acc, item), emptyNutrients());
}
export function kjToKcal(kj) {
    return kj / KJ_PER_KCAL;
}
export function kcalToKj(kcal) {
    return kcal * KJ_PER_KCAL;
}
/**
 * Energy implied by the macronutrient rows, used when a label or API record
 * omits the energy row entirely.
 */
export function energyFromMacros(nutrients) {
    return (nutrients.protein * KCAL_PER_GRAM.protein +
        nutrients.carbs * KCAL_PER_GRAM.carbs +
        nutrients.fat * KCAL_PER_GRAM.fat);
}
/**
 * Share of total energy contributed by each macronutrient, as percentages that
 * sum to 100. Returns zeroes when there is no energy to split.
 */
export function macroSplit(nutrients) {
    const proteinKcal = nutrients.protein * KCAL_PER_GRAM.protein;
    const carbsKcal = nutrients.carbs * KCAL_PER_GRAM.carbs;
    const fatKcal = nutrients.fat * KCAL_PER_GRAM.fat;
    const total = proteinKcal + carbsKcal + fatKcal;
    if (total <= 0)
        return { protein: 0, carbs: 0, fat: 0 };
    return {
        protein: (proteinKcal / total) * 100,
        carbs: (carbsKcal / total) * 100,
        fat: (fatKcal / total) * 100,
    };
}
function roundTo(value, decimals) {
    const factor = 10 ** decimals;
    // The +Number.EPSILON nudge avoids 1.005 -> 1.00 style float surprises.
    return Math.round((value + Number.EPSILON) * factor) / factor;
}
/**
 * Rounds for display: energy to whole kcal, everything else to one decimal.
 * Storage always keeps full precision — only round at the edges.
 */
export function roundNutrients(nutrients) {
    const result = emptyNutrients();
    for (const key of NUTRIENT_KEYS) {
        result[key] = key === 'kcal' ? Math.round(nutrients[key]) : roundTo(nutrients[key], 1);
    }
    return result;
}
//# sourceMappingURL=nutrients.js.map